import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { GoogleGenAI } from '@google/genai';
import Markdown from 'react-markdown';

interface ProductSummaryAIProps {
    productName: string;
    productDescription: string;
    attributes?: Record<string, string>;
}

const ProductSummaryAI: React.FC<ProductSummaryAIProps> = ({ productName, productDescription, attributes }) => {
    const [summary, setSummary] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasAttempted, setHasAttempted] = useState(false);

    useEffect(() => {
        // Reset state when product changes to allow manual trigger again
        setSummary(null);
        setHasAttempted(false);
    }, [productName]);

    const generateSummary = async () => {
        setIsLoading(true);
        setHasAttempted(true);
        try {
            let context = `Zrób krótkie, bardzo przekonujące, zwięzłe podsumowanie (max 3 zdania) 'Dlaczego warto kupić' dla produktu: ${productName}. Opis: ${productDescription}.`;
            if (attributes && Object.keys(attributes).length > 0) {
                context += ` Kluczowe cechy: ${Object.entries(attributes).map(([k,v])=>`${k}: ${v}`).join(', ')}.`;
            }
            context += ` Używaj formatowania markdown dla kluczowych zalet i czytelności. Zwróć tylko wygenerowany tekst bez zbędnych wstępów. Pisz w języku polskim.`;

            const payload = {
                model: 'gemini-3.1-flash-lite',
                contents: [{ role: 'user', parts: [{ text: context }] }]
            };

            const responseStream = await fetch('api/generate_ai.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!responseStream.ok) {
                let errText = await responseStream.text();
                throw new Error(`Błąd API: ${responseStream.status} ${errText}`);
            }

            const responseData = await responseStream.json();
            const responseText = responseData.candidates?.[0]?.content?.parts?.[0]?.text || 'Odkryj prawdziwą wartość tego produktu.';
            
            setSummary(responseText);
        } catch (error) {
            console.error("AI Summary Error:", error);
            setSummary("System tymczasowo niedostępny. Zaprezentowany produkt to doskonały wybór w swojej klasie.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 border border-indigo-100 rounded-xl mb-6">
            <div className="flex items-center gap-2 mb-2 text-indigo-800 font-bold text-sm">
                <Sparkles size={16} className="text-indigo-600" />
                <span>AI: Dlaczego warto?</span>
            </div>
            
            {!hasAttempted && !summary && (
                <button 
                    onClick={generateSummary}
                    className="text-xs bg-white border border-indigo-200 text-indigo-700 px-3 py-1.5 rounded hover:bg-indigo-50 transition-colors shadow-sm"
                >
                    Zapytaj AI o rekomendację
                </button>
            )}

            {isLoading && (
                <div className="flex items-center gap-2 text-indigo-600 text-xs">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Analizuję produkt...</span>
                </div>
            )}

            {summary && !isLoading && (
                <div className="text-sm text-gray-700 prose prose-sm prose-indigo leading-relaxed prose-p:my-1 prose-ul:my-1">
                    <Markdown>{summary}</Markdown>
                </div>
            )}
        </div>
    );
};

export default ProductSummaryAI;
